// "How did they find us?" — the client lead source picker (marketing
// foundation, CEO 2026-10-21). Required on New account; editable on the
// account Overview. The vocabulary is shared with the API (@merchd/shared).
// "Other" needs a short note; any source may carry one.
import { useQuery } from "@tanstack/react-query";
import { LEAD_SOURCE_LABEL, REP_PICKABLE_LEAD_SOURCES, type LeadSource } from "@merchd/shared";
import { api } from "@/lib/api";

export interface LeadSourceValue {
  source: LeadSource | "";
  detail: string;
}

/** Has the database step run (clients.lead_source exists)? Until it has, the
 *  question isn't asked and nothing is required (S1 review F2). Unknown while
 *  loading or on error → treated as not ready, so adding a client never blocks. */
export function useLeadSourceReady(): boolean {
  const q = useQuery({
    queryKey: ["clients", "lead-source-ready"],
    queryFn: () => api<{ ready: boolean }>("/clients/lead-source-ready"),
    staleTime: 5 * 60_000,
  });
  return q.data?.ready === true;
}

/** Ready to send? When the field is on: a source is picked, and Other has its note. */
export function leadSourceComplete(v: LeadSourceValue, ready = true): boolean {
  if (!ready) return true;
  return v.source !== "" && (v.source !== "other" || v.detail.trim().length > 0);
}

/** The POST /clients fields — only when the field is on. */
export function leadSourceBody(v: LeadSourceValue, ready: boolean): { lead_source?: string; lead_source_detail?: string } {
  if (!ready || v.source === "") return {};
  return { lead_source: v.source, ...(v.detail.trim() ? { lead_source_detail: v.detail.trim() } : {}) };
}

export function LeadSourceField({
  id,
  value,
  onChange,
  disabled,
  required = true,
  allowCommonsku = false,
  labelClassName = "block text-[11px] font-medium uppercase tracking-[0.12em] text-muted",
}: {
  id: string;
  value: LeadSourceValue;
  onChange: (v: LeadSourceValue) => void;
  disabled?: boolean;
  required?: boolean;
  /** Show "Existing client (from commonsku)" — only when it is already the value. */
  allowCommonsku?: boolean;
  labelClassName?: string;
}) {
  const options = allowCommonsku ? [...REP_PICKABLE_LEAD_SOURCES, "repeat_from_commonsku" as const] : REP_PICKABLE_LEAD_SOURCES;
  const needsNote = value.source === "other";
  return (
    <div>
      <label htmlFor={id} className={labelClassName}>
        How did they find us?
      </label>
      <select
        id={id}
        required={required}
        value={value.source}
        disabled={disabled}
        onChange={(e) => onChange({ ...value, source: e.target.value as LeadSource | "" })}
        className="mt-2 h-10 w-full rounded-md border border-rule bg-white px-3 text-sm text-ink disabled:opacity-60"
      >
        <option value="" disabled={required}>
          {required ? "Pick how this client found us" : "Unknown"}
        </option>
        {options.map((s) => (
          <option key={s} value={s}>
            {LEAD_SOURCE_LABEL[s]}
          </option>
        ))}
      </select>
      {value.source !== "" && (
        <input
          aria-label="Lead source note"
          value={value.detail}
          disabled={disabled}
          maxLength={500}
          onChange={(e) => onChange({ ...value, detail: e.target.value })}
          placeholder={needsNote ? "Required — e.g. the event, the person, the site" : "Optional note — e.g. who referred them, which show"}
          aria-required={needsNote}
          className="mt-2 h-10 w-full rounded-md border border-rule bg-white px-3 text-sm text-ink placeholder:text-[#8a8a85] disabled:opacity-60"
        />
      )}
      {needsNote && !value.detail.trim() && (
        <p className="mt-1 text-xs font-semibold text-risk">Add a short note when the source is Other.</p>
      )}
    </div>
  );
}
